import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Plus, Search, MoreVertical, Pencil, Trash2, Truck, Upload, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultCategories = [
  "Raw Materials",
  "Components",
  "Hardware",
  "Electrical",
  "Plumbing",
  "Tools",
  "Services",
  "Packaging",
  "Other",
];

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    category: "",
    notes: "",
  });

  const fetchSuppliers = useCallback(async () => {
    try {
      const [suppliersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/suppliers`, { withCredentials: true }),
        axios.get(`${API}/suppliers/meta/categories`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSuppliers(suppliersRes.data);
      // Merge fetched categories with defaults, filtering out empty/null values
      const fetchedCats = (categoriesRes.data || []).filter(c => c && c.trim());
      const allCategories = [...new Set([...defaultCategories, ...fetchedCats])];
      setCategories(allCategories);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast.error("Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.supplier_id}`, formData, {
          withCredentials: true,
        });
        toast.success("Supplier updated successfully");
      } else {
        await axios.post(`${API}/suppliers`, formData, {
          withCredentials: true,
        });
        toast.success("Supplier created successfully");
      }
      setSheetOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      console.error("Error saving supplier:", error);
      toast.error("Failed to save supplier");
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact_person: supplier.contact_person || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      address: supplier.address || "",
      category: supplier.category || "",
      notes: supplier.notes || "",
    });
    setSheetOpen(true);
  };

  const handleDelete = async () => {
    if (!supplierToDelete) return;
    try {
      await axios.delete(`${API}/suppliers/${supplierToDelete.supplier_id}`, {
        withCredentials: true,
      });
      toast.success("Supplier deleted successfully");
      setDeleteDialogOpen(false);
      setSupplierToDelete(null);
      fetchSuppliers();
    } catch (error) {
      console.error("Error deleting supplier:", error);
      toast.error("Failed to delete supplier");
    }
  };

  const handleDownloadTemplate = () => {
    const csv = "name,contact_person,email,phone,address,category,notes\nExample Supplier,John Doe,john@example.com,+27123456789,123 Main St,Raw Materials,Notes here";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "suppliers_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const handleImport = async () => {
    try {
      const lines = importData.trim().split("\n");
      if (lines.length < 2) {
        toast.error("No data to import");
        return;
      }

      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const suppliers = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map((v) => v.trim());
        if (values.length >= 1 && values[0]) {
          const supplier = {};
          headers.forEach((header, index) => {
            if (values[index]) {
              supplier[header] = values[index];
            }
          });
          if (supplier.name) {
            suppliers.push(supplier);
          }
        }
      }

      if (suppliers.length === 0) {
        toast.error("No valid suppliers found in data");
        return;
      }

      await axios.post(`${API}/suppliers/bulk`, { suppliers }, { withCredentials: true });
      toast.success(`Imported ${suppliers.length} suppliers`);
      setImportDialogOpen(false);
      setImportData("");
      fetchSuppliers();
    } catch (error) {
      console.error("Error importing suppliers:", error);
      toast.error("Failed to import suppliers");
    }
  };

  const resetForm = () => {
    setEditingSupplier(null);
    setFormData({
      name: "",
      contact_person: "",
      email: "",
      phone: "",
      address: "",
      category: "",
      notes: "",
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Raw Materials": "bg-blue-50 text-blue-700",
      "Components": "bg-purple-50 text-purple-700",
      "Hardware": "bg-slate-100 text-slate-700",
      "Electrical": "bg-amber-50 text-amber-700",
      "Plumbing": "bg-cyan-50 text-cyan-700",
      "Tools": "bg-orange-50 text-orange-700",
      "Services": "bg-green-50 text-green-700",
      "Packaging": "bg-pink-50 text-pink-700",
    };
    return colors[category] || "bg-slate-100 text-slate-700";
  };

  const filteredSuppliers = suppliers.filter((supplier) => {
    const matchesSearch =
      supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (supplier.contact_person &&
        supplier.contact_person.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (supplier.phone && supplier.phone.includes(searchQuery));
    const matchesCategory = categoryFilter === "all" || supplier.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Suppliers
          </h1>
          <p className="text-slate-500 mt-1">Manage your suppliers and vendors</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => setImportDialogOpen(true)}
            data-testid="import-suppliers-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button
            className="rounded-sm bg-slate-900 hover:bg-slate-800"
            onClick={() => setSheetOpen(true)}
            data-testid="add-supplier-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Supplier
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, contact, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-suppliers"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full md:w-48 rounded-sm">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Suppliers Table */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {filteredSuppliers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Contact Person</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSuppliers.map((supplier) => (
                  <TableRow 
                    key={supplier.supplier_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleEdit(supplier)}
                    data-testid={`supplier-row-${supplier.supplier_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                          <Truck className="h-5 w-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{supplier.name}</p>
                          {supplier.email && (
                            <p className="text-sm text-slate-500">{supplier.email}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {supplier.contact_person || "-"}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {supplier.phone || "-"}
                    </TableCell>
                    <TableCell>
                      {supplier.category ? (
                        <Badge className={`rounded-sm ${getCategoryColor(supplier.category)}`}>
                          {supplier.category}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(supplier)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSupplierToDelete(supplier);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <Truck className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No suppliers found
              </h3>
              <p className="text-slate-500 mb-6">
                {searchQuery || categoryFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Get started by adding your first supplier"}
              </p>
              {!searchQuery && categoryFilter === "all" && (
                <div className="flex gap-2 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setImportDialogOpen(true)}
                    className="rounded-sm"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Import from CSV
                  </Button>
                  <Button
                    onClick={() => setSheetOpen(true)}
                    className="rounded-sm bg-slate-900 hover:bg-slate-800"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Supplier
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Sheet */}
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) resetForm();
        }}
      >
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>
              {editingSupplier ? "Edit Supplier" : "Add Supplier"}
            </SheetTitle>
            <SheetDescription>
              {editingSupplier
                ? "Update supplier information"
                : "Add a new supplier to your list"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter company name"
                required
                className="rounded-sm"
                data-testid="supplier-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_person">Contact Person</Label>
              <Input
                id="contact_person"
                value={formData.contact_person}
                onChange={(e) =>
                  setFormData({ ...formData, contact_person: e.target.value })
                }
                placeholder="Enter contact name"
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Email address"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={formData.category || "none"}
                onValueChange={(value) =>
                  setFormData({ ...formData, category: value === "none" ? "" : value })
                }
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter address"
                rows={2}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes"
                rows={2}
                className="rounded-sm"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSheetOpen(false);
                  resetForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="save-supplier-btn"
              >
                {editingSupplier ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        setImportDialogOpen(open);
        if (!open) {
          setImportData("");
          setImportFile(null);
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Suppliers</DialogTitle>
            <DialogDescription>
              Import multiple suppliers from a CSV file or paste CSV data directly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="rounded-sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
            
            {/* File Upload */}
            <div className="space-y-2">
              <Label>Upload CSV File</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setImportFile(file);
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setImportData(event.target?.result || "");
                      };
                      reader.readAsText(file);
                    }
                  }}
                  className="rounded-sm"
                  data-testid="csv-file-input"
                />
              </div>
              {importFile && (
                <p className="text-sm text-emerald-600">
                  ✓ File loaded: {importFile.name}
                </p>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or paste CSV data</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>CSV Data</Label>
              <Textarea
                value={importData}
                onChange={(e) => {
                  setImportData(e.target.value);
                  setImportFile(null);
                }}
                placeholder="name,contact_person,email,phone,address,category,notes
Supplier A,John Doe,john@example.com,+27123456789,123 Main St,Raw Materials,Notes"
                rows={6}
                className="rounded-sm font-mono text-sm"
                data-testid="csv-data-textarea"
              />
            </div>
            <div className="p-3 bg-slate-50 rounded-sm">
              <p className="text-sm text-slate-600">
                <strong>Required columns:</strong> name
              </p>
              <p className="text-sm text-slate-600">
                <strong>Optional columns:</strong> contact_person, email, phone, address, category, notes
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setImportDialogOpen(false);
                setImportData("");
                setImportFile(null);
              }}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={!importData.trim()}
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Import Suppliers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Supplier</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{supplierToDelete?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Suppliers;
